<template>
  <el-dialog
    :title="title"
    :visible.sync="visible"
    width="60%"
    :before-close="handleClose"
  >
    <!-- 搜索区域 -->
    <div class="search-area" v-if="showSearch">
      <el-input
        v-model="searchKeyword"
        placeholder="请输入搜索关键词"
        style="width: 300px; margin-bottom: 15px"
        clearable
        @input="handleSearch"
      >
        <i slot="prefix" class="el-input__icon el-icon-search"></i>
      </el-input>
    </div>

    <!-- 表格区域 -->
    <el-table
      ref="table"
      :data="tableData"
      v-loading="loading"
      style="width: 100%"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
      :current-row-key="currentRowKey"
      :row-key="rowKey"
      :row-class-name="getRowClassName"
      highlight-current-row
    >
      <!-- 自定义选择列 -->
      <el-table-column
        width="55"
        align="center"
      >
        <template slot-scope="{ row }">
          <!-- 多选模式 - checkbox -->
          <div v-if="multiple" @click.stop @mousedown.stop>
            <el-checkbox
              :value="isRowSelected(row)"
              @input="(val) => handleCheckboxChange(row, val,'checkbox')"
            ></el-checkbox>
          </div>
          <!-- 单选模式 - radio -->
          <div v-else @click.stop @mousedown.stop>
            <el-radio
              v-model="currentRowKey"
              :label="row[rowKey]"
              @change="handleRadioChange(row)"
            />
          </div>
        </template>
      </el-table-column>

      <!-- 序号列 -->
      <el-table-column
        type="index"
        label="序号"
        width="60"
      ></el-table-column>

      <!-- 动态列 -->
      <el-table-column
        v-for="column in columns"
        :key="column.prop"
        :prop="column.prop"
        :label="column.label"
        :width="column.width"
        :formatter="column.formatter"
        show-overflow-tooltip
      ></el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="showPagination"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="pagination.current"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pagination.size"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total"
      style="margin-top: 20px; text-align: right"
    ></el-pagination>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'DialogList',
  props: {
    // 对话框标题
    title: {
      type: String,
      default: '选择数据'
    },
    // 是否显示对话框
    value: {
      type: Boolean,
      default: false
    },
    // 是否多选模式
    multiple: {
      type: Boolean,
      default: false
    },
    // 表格列配置
    columns: {
      type: Array,
      required: true
    },
    // 请求API函数
    apiFunction: {
      type: Function,
      required: true
    },
    // 额外的请求参数
    apiParams: {
      type: Object,
      default: () => ({})
    },
    // 是否显示搜索
    showSearch: {
      type: Boolean,
      default: false
    },
    // 是否显示分页
    showPagination: {
      type: Boolean,
      default: true
    },
    // 搜索字段名
    searchField: {
      type: String,
      default: 'keyword'
    },
    // 数据的唯一标识字段
    rowKey: {
      type: String,
      default: 'id'
    },
    // 已选中的数据
    selectedData: {
      type: [Array, Object],
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      tableData: [],
      searchKeyword: '',
      selectedRows: [],
      currentRowKey: null, // 单选模式下当前选中行的key
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      // 用于强制刷新checkbox状态的标识
      checkboxUpdateFlag: 0,
      // 防止重复点击的标识
      processingClick: false,
      // 最近的checkbox点击时间
      lastCheckboxClickTime: 0
    }
  },
  watch: {
    value: {
      handler(val) {
        this.visible = val
        if (val) {
          this.loadData()
          this.initSelectedData()
        }
      },
      immediate: true
    }
  },
  methods: {
    // 初始化已选数据
    initSelectedData() {
      if (this.multiple) {
        this.selectedRows = Array.isArray(this.selectedData) ? [...this.selectedData] : []
        this.$nextTick(() => {
          if (this.$refs.table) {
            this.selectedRows.forEach(row => {
              const tableRow = this.tableData.find(item => item[this.rowKey] === row[this.rowKey])
              if (tableRow) {
                this.$refs.table.toggleRowSelection(tableRow, true)
              }
            })
          }
        })
      } else {
        this.selectedRows = this.selectedData ? [this.selectedData] : []
        if (this.selectedData && this.selectedData[this.rowKey]) {
          this.currentRowKey = this.selectedData[this.rowKey]
        } else {
          this.currentRowKey = null
        }
      }
    },
    
    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          ...this.apiParams,
          current: this.pagination.current,
          size: this.pagination.size
        }
        
        // 添加搜索参数
        if (this.searchKeyword && this.searchField) {
          params[this.searchField] = this.searchKeyword
        }
        
        const response = await this.apiFunction(params)
        
        // 根据不同的响应格式处理数据
        if (response.data && response.data.records) {
          // 分页数据格式
          this.tableData = response.data.records
          this.pagination.total = response.data.total
        } else if (Array.isArray(response.data)) {
          // 简单数组格式
          this.tableData = response.data
          this.pagination.total = response.data.length
        } else {
          this.tableData = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 搜索处理
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.loadData()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.current = val
      this.loadData()
    },
    
    // 检查行是否被选中
    isRowSelected(row) {
      // 使用checkboxUpdateFlag来确保响应式更新
      this.checkboxUpdateFlag // 访问这个属性以触发依赖追踪
      const isSelected = this.selectedRows.some(item => item[this.rowKey] === row[this.rowKey])
      // console.log('检查行选中状态:', row[this.rowKey], '结果:', isSelected, '当前选中:', this.selectedRows.map(r => r[this.rowKey]))
      return isSelected
    },
    
    // 获取行的CSS类名
    getRowClassName({ row }) {
      if (this.isRowSelected(row)) {
        return 'selected-row'
      }
      return ''
    },
    
    // 处理checkbox选择变化
    handleCheckboxChange(row, checked,type) {
      // 防止重复点击
      if (this.processingClick) {
        console.log('正在处理点击，忽略重复事件')
        return
      }
      
      this.processingClick = true
      this.lastCheckboxClickTime = Date.now()
      console.log('checkbox点击:', type)
      
      if (checked) {
        // 添加到选中列表
        if (!this.isRowSelected(row)) {
          this.selectedRows = [...this.selectedRows, row]
        }
      } else {
        // 从选中列表移除
        const beforeLength = this.selectedRows.length
        this.selectedRows = this.selectedRows.filter(item => item[this.rowKey] !== row[this.rowKey])
      }
      // 更新checkboxUpdateFlag来触发视图更新
      this.checkboxUpdateFlag++
      // 重置处理标识
      this.$nextTick(() => {
        this.processingClick = false
      })
    },
    
    // 处理radio选择变化
    handleRadioChange(row) {
      // 如果当前已选中，再次点击则反选（取消选中）
      if (this.currentRowKey === row[this.rowKey]) {
        this.selectedRows = []
        this.currentRowKey = null
        console.log('单选反选:', row, 'rowKey:', this.currentRowKey)
      } else {
        this.selectedRows = [row]
        this.currentRowKey = row[this.rowKey]
        console.log('单选选中:', row, 'rowKey:', this.currentRowKey)
      }
    },
    
    // 多选模式下的选择改变（保留兼容性）
    handleSelectionChange(selection) {
      // 在多选模式下，不使用原生的selection-change事件
      // 改为完全使用自定义checkbox控制
      console.log('原生selection-change被触发，但在多选模式下被忽略:', selection)
    },
    
    // 行点击处理
    handleRowClick(row, column, event) {
      console.log('触发cell', row, this.multiple)
      
      // 如果点击的是checkbox列，不处理行点击
      if (column && column.type === 'selection') {
        console.log('点击了选择列，忽略行点击')
        return
      }
      
      // 如果最近500ms内有checkbox点击，忽略行点击
      const timeSinceLastCheckbox = Date.now() - this.lastCheckboxClickTime
      if (timeSinceLastCheckbox < 500) {
        console.log('最近有checkbox点击，忽略行点击')
        return
      }
      
      // 如果正在处理checkbox点击，不处理行点击
      if (this.processingClick) {
        console.log('正在处理checkbox点击，忽略行点击')
        return
      }
      
      if (this.multiple) {
        // 多选模式下，点击行切换checkbox状态
        const currentSelected = this.isRowSelected(row)
        this.handleCheckboxChange(row, !currentSelected,'cell')
      } else {
        // 单选模式下，选中当前行
        this.handleRadioChange(row)
      }
    },
    
    // 关闭对话框
    handleClose() {
      this.$emit('input', false)
      this.searchKeyword = ''
      this.selectedRows = []
      this.currentRowKey = null
    },
    
    // 确认选择
    handleConfirm() {
      // if (this.selectedRows.length === 0) {
      //   this.$message.warning('请选择数据')
      //   return
      // }
      
      if (this.multiple) {
        this.$emit('confirm', this.selectedRows)
      } else {
        this.$emit('confirm', this.selectedRows[0])
      }
      
      this.handleClose()
    }
  }
}
</script>

<style lang="less" scoped>
.search-area {
  margin-bottom: 15px;
}

.dialog-footer {
  text-align: right;
}

/deep/ .el-table__row {
  cursor: pointer;
}

/deep/ .el-table__row:hover {
  background-color: #f5f7fa;
}

/* 自定义Radio样式 */
/deep/ .el-radio {
  margin-right: 0;
  
  .el-radio__label {
    display: none; /* 隐藏label文本 */
  }
  
  .el-radio__input {
    cursor: pointer;
    
    .el-radio__inner {
      width: 16px;
      height: 16px;
      border: 2px solid #dcdfe6;
      border-radius: 50%;
      background-color: #fff;
      cursor: pointer;
      transition: all 0.3s;
      position: relative;
      
      &::after {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #409eff;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%) scale(0);
        transition: transform 0.15s ease-in;
      }
    }
    
    &.is-checked .el-radio__inner {
      border-color: #409eff;
      background-color: #fff;
      
      &::after {
        transform: translate(-50%, -50%) scale(1);
      }
    }
  }
  
  &:hover .el-radio__inner {
    border-color: #409eff;
  }
}

/* 自定义Checkbox样式 */
/deep/ .el-checkbox {
  margin-right: 0;
  
  .el-checkbox__label {
    display: none; /* 隐藏label文本 */
  }
  
  .el-checkbox__input {
    cursor: pointer;
    
    .el-checkbox__inner {
      width: 16px;
      height: 16px;
      border: 2px solid #dcdfe6;
      border-radius: 3px;
      background-color: #fff;
      cursor: pointer;
      transition: all 0.3s;
      position: relative;
      
      &::after {
        content: '';
        box-sizing: content-box;
        border: 2px solid #fff;
        border-left: 0;
        border-top: 0;
        height: 8px;
        left: 4px;
        position: absolute;
        top: 1px;
        transform: rotate(45deg) scaleY(0);
        width: 4px;
        transition: transform 0.15s ease-in 0.05s;
      }
    }
    
    &.is-checked .el-checkbox__inner {
      border-color: #409eff;
      background-color: #409eff;
      
      &::after {
        transform: rotate(45deg) scaleY(1);
      }
    }
  }
  
  &:hover .el-checkbox__inner {
    border-color: #409eff;
  }
}

/* 当前选中行样式 */
/deep/ .el-table .current-row {
  background-color: #ecf5ff !important;
}

/deep/ .el-table .current-row:hover {
  background-color: #ecf5ff !important;
}

/* 选中行的特殊标识 */
/deep/ .el-table tbody tr.selected-row {
  background-color: #f0f8ff;
}

/deep/ .el-table tbody tr.selected-row:hover {
  background-color: #e6f4ff;
}
</style> 